import React, { lazy, Suspense, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';

import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import styles from './styles.module.scss';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const TabsWithContent = ({ title = null, tabs = [], theme = '' }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const wistiaPlayerRefs = useRef([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    loop: false,
    dragFree: false,
  });
  const [isClient, setIsClient] = useState(false);
  const [loadedVideoTabs, setLoadedVideoTabs] = useState({});
  const [sectionInViewRef, isSectionInView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  // Inject images once when tabs arrive (containers are always rendered & stacked)
  useEffect(() => {
    if (!tabs || tabs.length === 0 || !containerRef?.current) return;

    tabs.forEach((tab) => {
      if (!tab?.image?.id) return;

      const parent = containerRef?.current?.parentElement;
      const source = parent.querySelector(`#twc-img-${tab.image.id}`);
      const target = parent.querySelector(`#tabs-with-content-img-box-${tab.image.id}`);

      if (source && target) {
        target.innerHTML = '';
        target.appendChild(source.cloneNode(true));
      }
    });
  }, [tabs]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isSectionInView) return;
    const currentTab = tabs?.[activeIndex];
    if (!currentTab?.videoId) return;

    setLoadedVideoTabs((prev) => {
      if (prev[activeIndex]) return prev;
      return { ...prev, [activeIndex]: true };
    });
  }, [activeIndex, isSectionInView, tabs]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;

    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const handleTabClick = useCallback(
    (index) => {
      if (emblaApi) {
        emblaApi.scrollTo(index, true, { duration: 600 });
      } else {
        setActiveIndex(index);
      }
    },
    [emblaApi]
  );

  const placeStyleToWistia = useCallback((wistiaElementParam, index) => {
    const wistiaElement = wistiaElementParam || wistiaPlayerRefs.current[index];
    // This runs after the Wistia player component has been rendered
    // setTimeout(() => {
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
            /* This CSS is now safely inside the Shadow DOM */
            video {
              width: 100% !important;
              height: 100% !important;
              object-fit: cover !important;
            }
            .click-for-sound-btn {
              display: none !important;
            }
        `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (index) => (event) => {
      const player = event?.target;
      if (!player) return;
      wistiaPlayerRefs.current[index] = player;
      placeStyleToWistia(player, index);
    },
    [placeStyleToWistia]
  );

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <section
      ref={(node) => {
        sectionInViewRef(node);
        containerRef.current = node;
      }}
      className={cn(styles.tabsWithContent, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
    >
      <div className="container">
        {title && (
          <div className={styles.header}>
            <Heading tag="h2" size="xl" className={styles.title}>
              {title}
            </Heading>
          </div>
        )}

        <div className={styles.tabsContainer}>
          <div className={styles.tabs} role="tablist">
            {tabs.map((tab, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-controls={`tabpanel-${index}`}
                id={`tab-${index}`}
                className={cn(styles.tab, {
                  [styles.active]: index === activeIndex,
                })}
                onClick={() => handleTabClick(index)}
              >
                {tab.tabName}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.embla} ref={emblaRef}>
          <div className={styles.emblaContainer}>
            {tabs.map((tab, index) => {
              const isActive = index === activeIndex;
              const isPhone = tab.buttonLink?.includes('tel:');
              return (
                <div
                  key={index}
                  className={styles.emblaSlide}
                  role="tabpanel"
                  id={`tabpanel-${index}`}
                  aria-labelledby={`tab-${index}`}
                >
                  <motion.div
                    className={styles.tabPanel}
                    initial={false}
                    animate={{
                      opacity: isActive ? 1 : 0,
                      scale: isActive ? 1 : 0.98,
                    }}
                    transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className={styles.tabContent}>
                      {tab.title && (
                        <Heading tag="h3" size="lg" className={styles.subtitle}>
                          {tab.title}
                        </Heading>
                      )}
                      {tab.description && (
                        <div
                          className={styles.description}
                          dangerouslySetInnerHTML={{ __html: tab.description }}
                        />
                      )}
                      {tab.buttonLabel && tab.buttonLink && (
                        <div className={styles.btnBox}>
                          <Button
                            theme="accent-primary"
                            size="sm"
                            to={tab.buttonLink}
                            className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
                          >
                            {isPhone && <PhoneIcon />}
                            {tab.buttonLabel}
                            {!isPhone && <ArrowRightIcon />}
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className={styles.mediaContainer}>
                      {tab.image?.id && !tab.videoId && (
                        <div
                          className={styles.imageWrapper}
                          id={`tabs-with-content-img-box-${tab.image.id}`}
                        />
                      )}
                      {isClient && tab.videoId && loadedVideoTabs[index] && (
                        <div className={styles.videoContainer}>
                          <Suspense fallback={null}>
                            <WistiaPlayerLazy
                              mediaId={tab.videoId}
                              onApiReady={handleApiReady(index)}
                              autoPlay
                              playlistLoop
                              muted
                              className={styles.wistiaPlayer}
                              poster={false}
                              swatch={false}
                              controls={false}
                              resumable
                            />
                          </Suspense>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

TabsWithContent.propTypes = {
  title: PropTypes.string,
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      tabName: PropTypes.string.isRequired,
      description: PropTypes.string,
      buttonLabel: PropTypes.string,
      buttonLink: PropTypes.string,
      image: PropTypes.shape({
        id: PropTypes.number,
      }),
      videoId: PropTypes.string,
    })
  ),
};

export default TabsWithContent;
